import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  BILLING_STATUS,
  ALL_PRODUCT_IDS,
  isDigitalGoodsSupported,
  getProductDetails,
  purchase as playPurchase,
  listPurchases as playListPurchases,
} from "@/lib/playBilling";

/**
 * React hook around the Play Billing adapter + backend verification.
 *
 * Entitlement is NEVER derived on the client. After a purchase or restore, the
 * hook re-fetches /api/auth/me and the backend's answer is what updates the UI.
 *
 * State shape:
 *   catalog: { status: loading|ready|unavailable|error, items?: {productId -> details} }
 *   purchasing: productId | null
 *   restoring: boolean
 *   lastError: string | null  (translation key suffix)
 */
export function usePlayBilling({ enabled = true } = {}) {
  const { checkAuth } = useAuth();
  const supported = isDigitalGoodsSupported();
  const [catalog, setCatalog] = useState({ status: supported ? BILLING_STATUS.LOADING : BILLING_STATUS.UNAVAILABLE });
  const [purchasing, setPurchasing] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [lastError, setLastError] = useState(null);
  const loadedRef = useRef(false);

  const loadCatalog = useCallback(async () => {
    if (!supported) {
      setCatalog({ status: BILLING_STATUS.UNAVAILABLE });
      return;
    }
    setCatalog({ status: BILLING_STATUS.LOADING });
    const res = await getProductDetails(ALL_PRODUCT_IDS);
    setCatalog(res);
  }, [supported]);

  useEffect(() => {
    if (!enabled || loadedRef.current) return;
    loadedRef.current = true;
    loadCatalog();
  }, [enabled, loadCatalog]);

  /** Map backend error payloads to a translation key suffix. */
  const errorKeyFromResponse = (e) => {
    const detail = e?.response?.data?.detail;
    const code = typeof detail === "object" ? detail?.code : null;
    switch (code) {
      case "billing_not_configured":
      case "billing_permission_error":
        return "verifyUnavailable";
      case "token_owned_by_other_user":
        return "tokenOtherAccount";
      case "subscription_not_active":
      case "subscription_expired":
        return "notActive";
      case "invalid_purchase_token":
      case "product_mismatch":
      case "invalid_product_id":
      case "product_not_allowed":
        return "verifyRejected";
      case "google_unavailable":
        return "googleUnavailable";
      default:
        return e?.response?.status === 401 ? "notSignedIn" : "verifyFailed";
    }
  };

  /**
   * Start a Google Play purchase for a product id, then verify server-side.
   * Returns { ok: boolean, status, errorKey? }.
   */
  const buy = useCallback(async (productId, { currency } = {}) => {
    setLastError(null);
    setPurchasing(productId);
    try {
      const result = await playPurchase(productId, { currency });
      if (result.status !== BILLING_STATUS.SUCCESS) {
        if (result.status === BILLING_STATUS.CANCELLED) return { ok: false, status: "cancelled" };
        const key = result.status === BILLING_STATUS.UNAVAILABLE ? "unavailable" : "purchaseFailed";
        setLastError(key);
        return { ok: false, status: result.status, errorKey: key };
      }
      // Backend verification is the only source of truth.
      try {
        const res = await api.verifyGooglePurchase(result.purchaseToken, productId);
        const verified = !!res?.data?.verified && !!res?.data?.user?.is_premium;
        await result.complete(verified ? "success" : "fail");
        if (!verified) {
          setLastError("verifyFailed");
          return { ok: false, status: "error", errorKey: "verifyFailed" };
        }
        await checkAuth();
        return { ok: true, status: "success", productId: res.data.product_id, expiry: res.data.expiry_time };
      } catch (e) {
        await result.complete("fail");
        const key = errorKeyFromResponse(e);
        setLastError(key);
        return { ok: false, status: "error", errorKey: key };
      }
    } finally {
      setPurchasing(null);
    }
  }, [checkAuth]);

  /**
   * Restore: listPurchases() from Play -> backend verification (idempotent).
   * Returns { ok, restored, errorKey? }.
   */
  const restore = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLastError(null);
    setRestoring(true);
    try {
      const listed = await playListPurchases();
      if (listed.status === BILLING_STATUS.UNAVAILABLE) {
        if (!silent) setLastError("unavailable");
        return { ok: false, restored: false, errorKey: "unavailable" };
      }
      const purchases = (listed.purchases || []).map((p) => ({ purchase_token: p.purchaseToken, product_id: p.itemId }));
      try {
        const res = await api.restoreGooglePurchases(purchases);
        const restored = !!res?.data?.restored;
        if (restored) await checkAuth();
        else if (!silent) setLastError(purchases.length ? "nothingActive" : "nothingToRestore");
        return { ok: true, restored, checked: res?.data?.checked ?? 0 };
      } catch (e) {
        const key = errorKeyFromResponse(e);
        if (!silent) setLastError(key);
        return { ok: false, restored: false, errorKey: key };
      }
    } finally {
      setRestoring(false);
    }
  }, [checkAuth]);

  return { supported, catalog, purchasing, restoring, lastError, loadCatalog, buy, restore, clearError: () => setLastError(null) };
}
